// Audit-workbench domain types (HUB-504). Ported from Doug's validated Claude
// Design prototype ("Coronis Coding Audit"). This is the anesthesia coding-audit
// review tool: an auditor compares two coded results for a case and records a
// verdict. Slice 1 (S0 + S1) lands the data foundation + the batch-queue screen.
//
// BLIND-REVIEW INVARIANT (a load-bearing product decision):
// Every case is coded twice — once by an autonomous AI agent, once by a human
// coder. The auditor must NEVER learn which displayed result is the agent and
// which is the human. So the shapes that reach the case/queue UI expose results
// only as opaque A/B slots (`CodedResultBlind`) with provenance withheld: no
// `agent`/`coder`/`isAgent`/`flip` field is reachable from a blind shape. The
// slot↔source mapping (and the per-case display flip that randomizes it) lives
// ONLY in the internal fixtures + the source's blinding step, and is used solely
// for later aggregate dashboards — never in the case or queue views.

/** Codebook classifications used across fields.
 *  `hcpcs` arrives only from the live source, which serves the loaded HCPCS
 *  Level II codeset; the local fixture has no HCPCS entries. It is in the union
 *  so KIND_LABEL is forced to carry a label for it — an unlabelled kind renders
 *  as a blank chip next to the code. */
export type CodeKind = "cpt" | "asa" | "hcpcs" | "icd" | "mod" | "phys";

/** One codebook entry — a code's human-readable title + description. */
export interface CodebookEntry {
  /** the code itself (populated on lookup miss so callers always have one) */
  code?: string;
  kind: CodeKind | "";
  title: string;
  desc: string;
  /** ASA base units (present on `kind: 'asa'` entries) */
  base?: number;
}

/** The per-field coded values for one result (agent or coder). Field keys match
 *  `AuditField.key`. Single-value fields are strings; multi fields are arrays;
 *  qualifier flags are booleans. This shape carries NO provenance. */
export interface CodedFields {
  surgical_cpt: string;
  anesthesia_cpt: string;
  primary_icd: string;
  additional_icd: string[];
  modifiers: string[];
  phys_status: string;
  /** anesthesia qualifier flags (compared A vs B like any field) */
  emergency: boolean;
  unusual_position: boolean;
}

// `ancillary` USED TO BE HERE and was removed, not renamed. Only the AI agent
// produced ancillary codes (the coding API emits ancillary_cpt_1..4; the human
// coder's FA feed has no ancillary source at all), so on any case with them the
// populated slot WAS the agent — a per-case unblinding oracle, and the
// arrangement is stable per case, so one such case generalises to every later
// one that reviewer sees. Symmetric absence is safe; asymmetric presence is
// not. It comes back when both legs can produce it, as a paired change with the
// server folds. See platform/internal/evalmaterializer/apply.go.

export type AuditFieldKey =
  | "surgical_cpt"
  | "anesthesia_cpt"
  | "primary_icd"
  | "additional_icd"
  | "modifiers"
  | "phys_status";

/** A reason a field could be flagged during review (verdict rationale library). */
export interface ReasonOption {
  id: string;
  label: string;
}

/** In-field help affordance ported from the prototype. */
export interface FieldHelp {
  tip: string;
  href: string;
}

/** A reviewable field definition (display order, kind, multiplicity, reasons). */
export interface AuditField {
  key: AuditFieldKey;
  label: string;
  kind: CodeKind;
  multi: boolean;
  reasons: ReasonOption[];
  help: FieldHelp;
  /** Shown and judgeable, but not required to submit and not scored.
   *
   * additional_icd and modifiers carry this (2026-08-26). Both are still worth
   * a reviewer's opinion, but neither is currently a fair comparison — the
   * agent contributes only its first comorbidity, and it infers modifiers where
   * FA only records them — so requiring a verdict on them spends reviewer time
   * on a number the study cannot defend. Mirrors `unscoredFields` in the Go
   * readmodel; the two must agree or the progress bar and the report disagree
   * about what a complete review is. */
  optional?: boolean;
}

/** An anesthesia qualifier flag (boolean field compared A vs B). */
export interface AuditQualifier {
  key: "emergency" | "unusual_position";
  label: string;
  sub: string;
}

/** Verdict tones map to the design-system semantic palette. */
export type VerdictTone = "green" | "blue" | "purple" | "red";

export type VerdictId = "both" | "a" | "b" | "neither";

/** The four possible per-field or per-case verdicts (used in later slices). */
export interface Verdict {
  id: VerdictId;
  label: string;
  short: string;
  tone: VerdictTone;
}

/** A coder (or the auditor) — display identity only in this tool. */
export interface Coder {
  id: string;
  name: string;
  initials: string;
  /** avatar hue (HSL degrees) ported from the prototype */
  hue: number;
}

/** The auditor's own identity. */
export interface Auditor extends Coder {
  title: string;
  credentials: string;
  email: string;
  team: string;
}

/** The clinical record a case is reviewed against (no true PHI — sample data). */
export interface CaseRecord {
  indication: string;
  procedure: string;
  findings: string;
  comorbidities: string[];
  airway: string;
  asaObserved: string;
  technique: string;
  agents: string;
  anesStart: string;
  anesStop: string;
  surgStart: string;
  surgStop: string;
}

/** Case identity + context shared by internal and blind shapes. */
interface AuditCaseCore {
  /**
   * The DCN — the case's KEY. What it is fetched, routed and stored by.
   *
   * NOT the identifier to put in front of a reviewer: a DCN is scoped per FA
   * job, so the same number belongs to different patients across clients
   * (HUB-633), and it cannot be looked up in FA. Use `caseLabel()` for anything
   * a person reads.
   */
  id: string;
  /**
   * FA's EPISODE_ID — the identifier a reviewer recognises and can carry back
   * into FA. What every screen labels the case with.
   *
   * Empty when no FA leg has landed (a PDF-only, agent-only case); `caseLabel()`
   * falls back to the DCN there rather than rendering a blank identity.
   */
  episodeId: string;
  batchId: string;
  patientFull: string;
  patient: string;
  mrn: string;
  dob: string;
  sex: string;
  ageYrs: number;
  dos: string;
  facility: string;
  surgeon: string;
  anesth: string;
  payer: string;
  service: string;
  priority: string;
  status: CaseStatus;
  receivedAgo: string;
  client: string;
  jobCode: string;
  /** the human coder assigned this case (display identity, NOT slot provenance) */
  coderId: string;
  record: CaseRecord;

  /**
   * Blob id of the source anesthesia-record PDF — the document this case's
   * codes were derived from, and the only thing a reviewer can actually check
   * them against.
   *
   * Empty means there is no stored document for this case: the no-blobstore dev
   * fallback, or a coder-only case that arrived from FA with no PDF. Empty must
   * render as "no document", NOT as the bundled synthetic sample — that sample
   * is labelled, but a labelled irrelevant document is still not the record.
   */
  documentBlobId: string;
}

/**
 * Sentinel meaning "render the bundled synthetic sample record". Only the mock
 * data source uses it, so the sample can never stand in for a real case's
 * document: a live case with no stored PDF has an empty documentBlobId and
 * renders as "no document".
 */
export const SAMPLE_DOCUMENT_BLOB_ID = "__sample__";

export type CaseStatus = "unreviewed" | "submitted";

/** How the evaluator left a case. `submitted` = a completed evaluation; `draft` =
 *  saved-and-set-aside to finish later; the rest are routing dispositions that move
 *  the case out of the evaluator's queue without a completed evaluation. */
export type CaseDisposition =
  | "submitted"
  | "draft"
  | "missing_information"
  | "incomplete_documentation"
  | "needs_escalation";

/**
 * INTERNAL case shape — carries provenance (`agent` vs `coder` results + the
 * display `flip`). NEVER hand this to a case/queue view. It exists only in the
 * fixtures and is consumed by the data source's blinding step + (later) the
 * aggregate dashboards.
 */
export interface AuditCaseInternal extends AuditCaseCore {
  /** result coded by the autonomous AI agent */
  agent: CodedFields;
  /** result coded by the human coder */
  coder: CodedFields;
  /** which display slot each true source lands in (stable per case id) */
  flip: boolean;
}

/**
 * BLIND result pair — the only result shape a case/queue view ever sees. `a` and
 * `b` are opaque slots; nothing here says which is the agent. Provenance withheld.
 */
export interface CodedResultBlind {
  a: CodedFields;
  b: CodedFields;
}

/**
 * BLIND case — case identity + record + the opaque A/B result pair. Deliberately
 * has NO `agent`/`coder`/`isAgent`/`flip` field.
 */
export interface AuditCase extends AuditCaseCore {
  /**
   * Whether each opaque slot holds a result at all.
   *
   * Distinct from an EMPTY result: the wire sends an empty CodedFields for an
   * absent slot, so without these the view cannot tell "coded, with no value
   * for this field" from "never coded". The compare screen showed
   * "— not billed —" on every field of a one-sided case with nothing saying
   * why, and that is most cases today.
   *
   * Named by SLOT, never by source. Which slot tends to be the empty one is an
   * inference the reviewer can already draw from the screen; this must not
   * spell it out for them.
   */
  hasA: boolean;
  hasB: boolean;

  /** FA episode context; undefined when no coder leg has arrived for the case. */
  episode?: EpisodeContext;
  /** Patient identity; undefined when the caller's grant does not confer PHI.
   *  Undefined is "not permitted", NOT "no patient" — the review header must
   *  say which, or a reviewer routes a case that was never incomplete. */
  patient_context?: PatientContext;

  results: CodedResultBlind;
}

/** Batch lifecycle state derived from case review progress. */
export type AuditBatchState = "new" | "active" | "complete";

/**
 * A lightweight, blind case summary for the queue + batch case list. Carries the
 * searchable identity and a per-case discrepancy COUNT (a count reveals no slot
 * provenance) — but never the A/B result values themselves.
 */
/**
 * FA's non-PHI context for one episode.
 *
 * No procedure narrative: FA's flattened episode query carries ids and codes,
 * not text. The surgical CPT the two sides assigned is the nearest thing and it
 * lives in the blind A/B slots, where deriving a "procedure" from one of them
 * would name that slot.
 */
export interface EpisodeContext {
  facilityId: string;
  /** FA's name for `facilityId`. EMPTY is ordinary — FACILITY_NAME is
   *  nullable in FA — so render `facilityLabel()`, never this alone. */
  facilityName: string;
  serviceStartDate: string;
  serviceEndDate: string;
  placeOfService: string;
  operatingRoom: string;
  anesthesiaType: string;
  providerIdOfRecord: string;
  crnaIdOfRecord: string;
  coderId: string;
  /** coderId resolved through FA's user view. Empty when the id did not
   *  resolve, in which case the id itself is what there is to show. */
  coderName: string;
  codedWhen: string;
  prStatus: string;
  cdStatus: string;
  peStatus: string;
}

/**
 * Who the case is about. PHI.
 *
 * Present only when the caller's grant confers it. `undefined` therefore means
 * "not permitted", NOT "no patient" — the two must render differently, or a
 * permissions outcome reads as missing data and a reviewer routes a case that
 * was never incomplete.
 *
 * No first name and no date of birth. Both arrive redacted from FA and the
 * server has no field for either: the DOB is year-shifted, and an age derived
 * from it is a plausible number that is simply false on the one screen where
 * age changes the correct code.
 */
export interface PatientContext {
  lastName: string;
  mrn: string;
  gender: string;
  accountNumber: string;
  payer: string;
  payerPlan: string;
}

export interface AuditBatchCaseSummary {
  /** The DCN — the case's key. See AuditCaseCore.id; use `caseLabel()` to
   *  display a case, never this. */
  id: string;
  /** FA's EPISODE_ID. Empty for a PDF-only case — see AuditCaseCore.episodeId. */
  episodeId: string;
  patient: string;
  patientFull: string;
  mrn: string;
  payer: string;
  procedure: string;
  status: CaseStatus;
  coderId: string;
  /** FA episode context; undefined when no coder leg has arrived for the case. */
  episode?: EpisodeContext;
  /** Patient identity; undefined when the caller's grant does not confer PHI. */
  patient_context?: PatientContext;
  /** Number of compared fields where the two results differ.
   *  MEANINGLESS unless `comparable` — see below. */
  discrepancyCount: number;
  /**
   * Whether the two sides could be compared at all, i.e. both exist.
   *
   * The load-bearing field. `discrepancyCount === 0` means "identical" only
   * when this is true; otherwise it means "nothing was compared", and the queue
   * rendered both as a green "Full match" badge — asserting on every live case
   * that the AI and the human agreed completely, having compared none of them.
   */
  comparable: boolean;
  /** Denominator for the count, from the server so the view stops counting its
   *  own copy of the field list. */
  comparedFieldCount: number;
  /**
   * The surgical CPT for each BLIND display slot (already flipped, provenance
   * withheld). A one-field A/B teaser for the batch case table — showing two
   * opaque code strings reveals no slot provenance (which is the agent). */
  surgical: { a: string; b: string };
}

/**
 * A batch is FA's own batch: one day's work for one client. NOT one facility —
 * facility is per EPISODE and a batch may span several, which is why there is
 * no batch-level facility here. The column that showed one was always empty.
 *
 * Aggregates ported from the
 * prototype's `buildBatches`. Carries only counts + blind case summaries.
 */
/**
 * A reviewer queue, as ListQueues returns it.
 *
 * The queue id is the ACCESS SCOPE KEY — a reviewer sees a batch because their
 * queue names its id (see evalreview.proto). So this is not decoration: it is
 * the authorization fact rendered, and it is why a shared queue must never be
 * shown as though it belonged to one person.
 */
export interface AuditQueue {
  id: string;
  /** Display name; falls back to the id when the server has none. */
  name: string;
  batchIds: string[];
  reviewerIds: string[];
  /**
   * Display identity for the ids in reviewerIds, resolved server-side.
   *
   * reviewerIds are opaque principal ids, so rendering assignment from them
   * alone shows "01a01b6e-4537-7ea3-81d6-8a76cd0833eb" where a person's name
   * belongs — which does not answer the question assignment exists to answer.
   *
   * Possibly SHORTER than reviewerIds, and possibly absent: the server omits
   * principals it cannot resolve rather than echoing their ids back as names.
   * reviewerIds stays authoritative for membership.
   */
  reviewers?: { id: string; displayName: string }[];
}

export interface AuditBatch {
  id: string;
  /** Primary batch identifier (RCM-style, e.g. `B-2026-0612-01`): date +
   *  per-day sequence. Leads the batch in the card, table, detail header, and
   *  breadcrumb; also a searchable field. */
  batchNumber: string;
  /** FA's own BATCH_CODE. The identifier a reviewer recognises, and what FA's
   *  coding batch list leads with. Empty for ingest-day buckets. */
  batchCode: string;
  /** The batch's date, formatted for display. NEVER sort on this — see dosSort. */
  dos: string;
  /**
   * The same date as epoch milliseconds, for ordering.
   *
   * `dos` is locale-formatted ("8/7/2026"), and the list used to sort on it
   * lexically — which put 8/7 after 8/18. A separate numeric field rather than
   * re-parsing the display string, because parsing a locale format is
   * ambiguous by construction (8/7 is August 7th or July 8th depending on who
   * is looking) and a wrong order is invisible.
   *
   * 0 when the batch has no usable date, which sorts last under `desc`.
   */
  dosSort: number;
  client: string;
  jobCode: string;
  caseCount: number;
  reviewedCount: number;
  remainingCount: number;
  /** Cases in the batch with at least one field discrepancy. */
  discrepancyCaseCount: number;
  /** Cases where both sides exist, so agreement was measurable at all. Most
   *  cases have one side today, and a zero discrepancy count over zero
   *  comparable cases is not agreement. */
  comparableCaseCount: number;
  /** distinct human-coder ids across the batch's cases */
  coders: string[];
  /** Coder names, positionally aligned with `coders` and possibly SHORTER — an
   *  id FA could not resolve contributes none. Index into it; never zip. */
  coderNames: string[];
  /**
   * How many of the batch's cases each coder in `coders` worked, positionally
   * aligned with it.
   *
   * What group-by-coder needs to not double-count. A batch is worked by three
   * to five coders, so grouping on `coders` alone lists one batch under each of
   * them; this is what lets the list attribute a batch to whoever did most of
   * it. Does NOT sum to `caseCount` — an uncoded case belongs to no coder.
   *
   * Possibly SHORTER than `coders` against an older server. Index into it and
   * treat a missing entry as 0; never zip.
   */
  coderCaseCounts: number[];
  state: AuditBatchState;
  /** FA's own batch status, RAW ('C' / 'P'). `state` above is derived from OUR
   *  review progress and reads "Not started" on every batch until a reviewer
   *  begins; this is the only FA batch column measured to vary. Undefined for
   *  ingest-day buckets, which have no FA batch record.
   *
   *  Not mapped to a label: what 'C' and 'P' mean is unconfirmed with FA. */
  faStatus?: string;
  /**
   * Where the batch stands with FA's own coders.
   *
   * `activeEpisodes` is what FA holds that is still live; `codedEpisodes` how
   * many a human has finished; `inactiveEpisodes` those withdrawn. `uncoded` is
   * derived and clamped — FA evaluates active and coded as separate subqueries
   * against a moving table, so coded can briefly exceed active and a negative
   * count would render as a bug in the screen rather than a race in the source.
   *
   * These are FA's counts for the WHOLE batch. `caseCount` is what this
   * reviewer can see. They are allowed to differ, and the batch list says so
   * rather than implying the batch is short.
   */
  activeEpisodes: number;
  codedEpisodes: number;
  uncodedEpisodes: number;
  inactiveEpisodes: number;
  /** Every active episode is coded. False for an empty batch: that is not
   *  finished work, and treating it as such floats empty batches to the top of
   *  a queue filtered to reviewable batches. */
  codingComplete: boolean;
  /** FA's DECLARED document count (BATCH_COUNT). Shown beside the observed
   *  figure, never instead of it: a mismatch means episodes are missing, and a
   *  reviewer shown one number reads a short batch as small. */
  declaredCount: number;
  /** BATCH_CD_STATUS_CODE — the coding stage specifically, as opposed to
   *  faStatus, which stays open while payment posting is outstanding. */
  codingStatus?: string;
  /** When FA closed the batch, and when its QC is due. Display strings; empty
   *  when FA has no value or the value would not parse. Carried because FA's
   *  own coding batch list leads with dates a coder plans around. */
  closeDate: string;
  qcDueDate: string;
  /** Same dates as epoch ms for ordering, 0 when absent. Never sort on the
   *  display strings — that is what put 8/7 after 8/18. */
  closeDateSort: number;
  qcDueDateSort: number;
  cases: AuditBatchCaseSummary[];
}

/** A codebook search hit (identity + classification only). */
export interface CodebookHit {
  code: string;
  kind: CodeKind | "";
  title: string;
  desc: string;
}

/** One code's adjudication inside a multi-value field, as submitted. */
export interface SubmittedCodeVerdict {
  code: string;
  correct: boolean;
  reason?: string;
  note?: string;
  /** the reviewer supplied this code because neither result carried it */
  added?: boolean;
}

/**
 * One field's judgement as it goes to the server.
 *
 * `codes` is the reviewer's actual work on a multi-value field — they decide
 * code by code — and `verdict` is the SUMMARY the study scores. Carrying only
 * the summary keeps "the two lists disagreed" and drops which code was wrong
 * and why, which is the half anyone can act on.
 */
export interface SubmittedFieldVerdict {
  verdict: VerdictId;
  reasons: string[];
  note?: string;
  codes?: SubmittedCodeVerdict[];
}

/** Verdict payload the reviewer submits. */
export interface AuditSubmission {
  caseId: string;
  /** per-field verdict, keyed by AuditFieldKey plus the two qualifier keys */
  fieldVerdicts?: Record<string, SubmittedFieldVerdict>;
  note?: string;
  /** how the evaluator left the case (defaults to a completed submission) */
  disposition?: CaseDisposition;
}

/** Aggregate org-level dashboard (blind-safe — provenance used only here). */
/**
 * Agreement measured directly between the two coded results, with NO reviewer
 * input.
 *
 * Everything else on OrgDashboard is reviewer-verdict based, so until auditors
 * submit, the report screen is empty — which is exactly the phase where a study
 * most needs to know whether its corpus is sound. Reviewing is expensive, and
 * pointing auditors at a corpus whose agreement rate is a measurement artefact
 * wastes it.
 *
 * These numbers say nothing about who is RIGHT. Two sides agreeing on a wrong
 * code agree; that is what the reviewer verdicts above are for.
 */
export interface CorpusAgreement {
  /** Every case in the store. */
  cases: number;
  /**
   * Cases with both sides present and not withheld — the denominator every
   * rate here is over. Quoting one over `cases` instead would understate
   * agreement by the entire exclusion set.
   */
  comparable: number;
  /** Comparable cases where no field DIFFERED. A field that could not be
   *  compared does not spoil a full agreement. */
  fullyAgreed: number;
  /**
   * Why cases were not comparable, most common first. Broken out rather than
   * summed because the remedies differ: not-billable is the source saying there
   * is nothing to bill, and a missing side is work that has not arrived.
   *
   * The reasons are server-phrased and deliberately avoid the coding agent's
   * own vocabulary — naming a slot would tell an auditor which leg is the AI,
   * and the arrangement is stable per case.
   */
  exclusions: { reason: string; cases: number }[];
  /**
   * Per-field agreement. `notComparable` is its own column and must stay that
   * way in the view: folding it into either other one is a claim the data does
   * not support. A modifier only one side populated is one side answering a
   * question the other was not asked.
   */
  fields: {
    field: string;
    agree: number;
    differ: number;
    /** One side produced codes and the other produced none — a coverage gap,
     *  not a coding disagreement. Counted inside `differ` until 2026-08-26,
     *  which put every declined case into the headline disagreement rate.
     *  Excluded from the agreement denominator: a case only one side answered
     *  says nothing about who codes better. Says one side, never which. */
    oneSided: number;
    notComparable: number;
  }[];
  /**
   * Per-code counts, most-seen first. `seen` is comparable cases where EITHER
   * side carried the code; `matched` is where both did.
   *
   * The code is the COMPARISON key, so `45378` and `45378DG` are one row and an
   * FY2026 ICD expansion is not a different diagnosis from its parent —
   * counting raw strings would split one code across rows and understate each.
   *
   * Unlike `erroredCodes`, nothing here is attributed to a side, so no rarity
   * floor is needed: seen/matched cannot reveal which slot is the agent.
   */
  codes: {
    field: string;
    code: string;
    seen: number;
    matched: number;
    /** A few cases carrying this code, to open rather than infer. Present only
     *  on this SYMMETRIC table — seen/matched never say which side had the code,
     *  so naming a case beside them reveals nothing about its A/B arrangement.
     *  The errored-code table carries agentWrong/coderWrong and therefore has no
     *  equivalent, by design. */
    exampleDcns: string[];
  }[];
  /** Rows the server's cap removed. Rendered, so the table reads as a top-N
   *  rather than as every code the study has ever seen. */
  codesSuppressed: number;
}

/**
 * One day or ISO week of cases, bucketed by RECEIVED date (not reviewed —
 * reviewers judge old cases late, so a review-date series would confound a
 * model change with a backlog catch-up).
 *
 * `measured` is the blind-review floor: it is false, and the verdict counters
 * are all zero, unless the bucket holds enough reviews from enough distinct
 * reviewers to report without exposing an individual reviewer's tally. It
 * exists so the trend chart can render an unmeasured bucket as a GAP rather
 * than as a flat 0% — the same "not measured vs measured zero" distinction
 * `OrgDashboard.agreement` makes at the study level, but per-bucket.
 */
export interface TrendBucket {
  /** YYYY-MM-DD in the zone the request was sent with. */
  start: string;
  casesReceived: number;
  comparable: number;
  fullyAgreed: number;
  reviewed: number;
  measured: boolean;
  judged: number;
  agentCorrect: number;
  coderCorrect: number;
  both: number;
  neither: number;
  /**
   * One entry per scored field, in the server's scored-field order, on every
   * bucket that saw a case. A zero-filled gap bucket carries none — an ABSENT
   * entry is not a measured zero, and must render the way an unmeasured
   * bucket does.
   */
  fields: FieldTrend[];
}

/**
 * One scored field's slice of a `TrendBucket`.
 *
 * The bucket totals average every field together, so a field only shows up in
 * them if its movement is big enough to move the study-wide number — a
 * surgical-CPT regression and a physical-status improvement in the same week
 * cancel and are both invisible.
 *
 * The two halves follow the bucket's own rules. The verdict counters obey the
 * blind-review floor exactly as the bucket totals do (zero on an unmeasured
 * bucket; a week is the sum over its measured days), so no field can publish a
 * verdict the total does not. The corpus counters are never floored: an
 * agreement is computed from the two coded results alone, is symmetric, and
 * exists before any reviewer looks at the case.
 */
export interface FieldTrend {
  field: string;
  judged: number;
  agentCorrect: number;
  coderCorrect: number;
  both: number;
  neither: number;
  agree: number;
  differ: number;
  oneSided: number;
  notComparable: number;
}

/** One day or ISO week of reviews, bucketed by SUBMITTED date — throughput,
 *  as distinct from the received-date series above. */
export interface ThroughputBucket {
  start: string;
  reviewsSubmitted: number;
}

/** The trend charts' full series: received-case buckets and review-throughput
 *  buckets, each daily and weekly. */
export interface MetricsTrend {
  daily: TrendBucket[];
  weekly: TrendBucket[];
  reviewsDaily: ThroughputBucket[];
  reviewsWeekly: ThroughputBucket[];
}

/** One code a reason was cited against, ranked. */
export interface ReasonCodeCount {
  field: string;
  code: string;
  count: number;
}

/** A ranked, counted reason — study-wide or per-field depending on where it
 *  appears — with the codes it was cited against. */
export interface ReasonCount {
  reason: string;
  count: number;
  codes: ReasonCodeCount[];
}

/**
 * One code judged wrong, with the side that produced it, why (ranked
 * reasons), and a few example cases to open rather than infer.
 *
 * `exampleDcns` is AUDITOR-ONLY on the wire: a case id beside agentWrong /
 * coderWrong says which slot of that case is the agent, so a caller without
 * the all-batches grant never receives it — this shape just carries whatever
 * the server sent, which may be empty.
 */
export interface ErroredCode {
  code: string;
  field: string;
  agentWrong: number;
  coderWrong: number;
  wrong: number;
  reasons: { reason: string; count: number }[];
  exampleDcns: string[];
}

/**
 * Which drill-in table a code-cases query is against: `corpus` is the
 * symmetric seen/matched table (either side carried the code), `errored` is
 * the side-attributed wrong-code table. The two modes carry different
 * fields on the returned rows (see CodeCaseRow) and map to
 * `CodeCaseMode.CORPUS` / `.ERRORED` on the wire.
 */
export type CodeCaseMode = "corpus" | "errored";

/** The drill-in query behind one report row. Carries identifiers only — a
 *  mode, a field, a code, a reason id — never a payload, so it is safe to put
 *  straight into a query key. */
export interface CodeCaseQuery {
  mode: CodeCaseMode;
  field?: string;
  code?: string;
  reason?: string;
}

/**
 * One case behind a drill-in row.
 *
 * `id` is the DCN — auditor-only, and only reachable through this
 * already-gated query (ListCodeCases refuses a caller without the
 * all-batches grant with PermissionDenied). `receivedAt` /
 * `reviewSubmittedAt` are full ISO 8601 strings (not locale-formatted): the
 * CSV export needs an unambiguous form, and the table formats for display
 * separately. Empty string means the wire timestamp was absent or zero, not
 * the UNIX epoch's predecessor.
 */
export interface CodeCaseRow {
  id: string;
  episodeId: string;
  batchId: string;
  receivedAt: string;
  receivedAtEpoch: number;
  reviewSubmittedAt: string;
  field: string;
  code: string;
  /** ERRORED only: which side was judged wrong. Empty in CORPUS mode, where
   *  nothing here may say which slot is the agent. */
  side: "agent" | "coder" | "";
  verdict: string;
  reasons: string[];
  /** CORPUS only: whether both sides carried the code. */
  matched: boolean;
}

export interface CodeCaseList {
  rows: CodeCaseRow[];
  total: number;
}

export interface OrgDashboard {
  reviewed: number;
  thisWeek: number;
  /**
   * Field-level judgements — the denominator the rates are actually over.
   *
   * Separate from `reviewed` (distinct cases) because quoting a field-level n
   * as a case count overstates the sample by roughly the number of fields per
   * case, and "88% over 210 judgements" is a materially weaker claim than
   * "88% over 210 cases".
   */
  judged: number;
  /** judgements where the reviewer accepted both sides. */
  both: number;
  /** judgements where the reviewer rejected both sides. */
  neither: number;
  /**
   * Reviews skipped because they were resolved under a superseded study key,
   * and so describe a slot mapping that no longer holds.
   *
   * Carried all the way to the view rather than dropped at the mapper: a
   * non-zero value means the denominator moved, and a dashboard that quietly
   * omits it reports a rate over a corpus the reader believes is complete.
   */
  excluded: number;
  /**
   * Rates in [0,1], or -1 meaning NOT MEASURED.
   *
   * A plain 0 cannot distinguish "the agent got nothing right" from "nobody has
   * reviewed anything", and on an unstarted study the second renders as the
   * first — a dashboard confidently reporting 0% accuracy for a model that has
   * never been judged. -1 forces the UI to decide.
   */
  agreement: number;
  agentAccuracy: number;
  coderAccuracy: number;
  /**
   * Judgements where the auditor NAMED a correct answer — the denominator the
   * selection rates below are over, and the flag that says whether they were
   * measured at all.
   *
   * Separate from `judged`: a "neither" verdict counts here only when the
   * auditor recorded the code that should have been used instead, in which case
   * it is a resolved selection BOTH sides missed. A "neither" with no
   * replacement on file names no answer, so nothing can be matched against it
   * and it lands in `selectionUnresolved`. Zero here means the selection rates
   * are NOT MEASURED even when `judged` is large.
   */
  selectionResolved: number;
  /**
   * Judgements where the auditor rejected both sides and recorded no
   * replacement code.
   *
   * Shown beside the rates, not folded into them. While this is non-zero the
   * selection rates remain an UPPER BOUND — each of these judgements would
   * enter the denominator without entering either numerator — so they must not
   * be quoted as final. It is no longer the whole "neither" population: a
   * rejection where the auditor named the correct code is captured end to end
   * and resolves.
   */
  selectionUnresolved: number;
  /**
   * Share of resolved selections that were the agent's code, in [0,1], or -1
   * for NOT MEASURED.
   *
   * A different question from `agentAccuracy`, and the difference is the
   * denominator: accuracy divides by every judgement, so a case the record
   * could not support counts against the agent as hard as one the human coder
   * won. This divides only by judgements where the auditor named an answer.
   */
  agentSelectionMatch: number;
  /** The same measure for the human baseline — the number that gives the
   *  agent's any meaning. */
  coderSelectionMatch: number;
  /**
   * The specific codes judged wrong, worst first, with the side that produced
   * them. The actionable half of this screen: a field rate says the agent is
   * weak on modifiers, this says which modifier.
   *
   * Server-floored — codes seen erring only once or twice are withheld, because
   * a single-case row is not an aggregate and would let a reviewer who just
   * judged that case work out which slot is the agent.
   */
  erroredCodes: ErroredCode[];
  /** Distinct field+code pairs the floor removed. Rendered, so the list reads
   *  as "the recurring offenders" rather than as everything that went wrong. */
  erroredCodesSuppressed: number;
  /** Rows that cleared the floor but fell past the server's cap — a different
   *  reason to be missing than `erroredCodesSuppressed`, and rendered
   *  separately so the list reads as a top-N rather than the whole floored
   *  set. */
  erroredCodesCapped: number;
  avgReviewMin: string;
  /**
   * Per-field breakdown in PROVENANCE terms (agent/coder), not slot terms.
   *
   * The org dashboard is the one surface that speaks in provenance, and it is
   * safe because it is an aggregate — "the agent is at 78% on primary_icd"
   * reveals nothing about the case a reviewer currently has open. Contrast
   * MyDashboard.verdictMix, which stays in slot terms (a/b) precisely because
   * it is a reviewer's own tally and naming sides there would leak provenance
   * for the cases they personally reviewed.
   *
   * This previously carried a/b, which was inconsistent with the agent/coder
   * rates beside it and would have shown a reviewer slot letters that mean
   * nothing at study level.
   */
  fields: {
    field: string;
    agentCorrect: number;
    coderCorrect: number;
    both: number;
    neither: number;
    judged: number;
    /** Ranked, counted reasons cited on THIS field. The study-wide roll-up
     *  cannot answer "why does the ASA code go wrong" — a reason that dominates
     *  one field is diluted there by every field that never cites it. */
    topReasonCounts: ReasonCount[];
  }[];
  /**
   * Study-wide reason ranking with counts.
   *
   * Counted, and study-wide, for a reason: this used to be the per-field top-3
   * lists concatenated, which is not a ranking of anything. Those lists are each
   * truncated, so a reason cited steadily but never in a field's top three
   * vanished entirely, while a reason cited once on a rare field survived — and
   * with no counts a reader had no way to see it happening.
   */
  topReasons: ReasonCount[];
  /**
   * Cases-received and reviews-submitted trend, daily and weekly. Populated
   * in BOTH the has-data and empty branches of the mapper: the daily/weekly
   * receipt buckets exist before any reviewer has submitted a verdict, and a
   * dashboard that only fills this in once `hasData` is true would show a
   * flat line for the entire pre-review phase the trend chart is most useful
   * during.
   */
  trend: MetricsTrend;
  /**
   * Agreement with no reviewer input. Populated even when every rate above is
   * NOT MEASURED — that is the point of it.
   */
  corpus: CorpusAgreement;
  /**
   * The study-wide blind-review floor — the same one `TrendBucket.measured`
   * applies per bucket, applied once to the totals. `cleared` is false until
   * `reviewed` (not `judged` — a count carries no provenance) reaches
   * `minReviews` reviewed cases from `minReviewers` distinct reviewers; below
   * it every field above from `judged` through `topReasons` is zero/empty and
   * `agreement`/`agentAccuracy`/etc. read as NOT MEASURED, for the same
   * self-identification reason a single-reviewer trend bucket is withheld: a
   * reviewer who just judged the one case behind the total could otherwise
   * read "agent accuracy 0%" back as "the case I judged was the agent".
   */
  verdictFloor: { cleared: boolean; minReviews: number; minReviewers: number };
}

/** The auditor's personal weekly stats. */
export interface MyDashboard {
  weekLabel: string;
  reviewedWeek: number;
  reviewedTarget: number;
  reviewedLastWeek: number;
  reviewedToday: number;
  avgReviewMin: string;
  avgLastWeek: string;
  queueRemaining: number;
  agreementRate: number;
  submittedTotal: number;
  dayLabels: string[];
  dayCounts: number[];
  dayTarget: number;
  verdictMix: { both: number; a: number; b: number; neither: number };
  fieldsFlagged: { label: string; count: number }[];
  topReasons: { label: string; count: number }[];
  recent: { id: string; patient: string; when: string; flags: number }[];
  streak: number;
  rank: number;
  teamSize: number;
}
